import React from 'react';
import { Button, Section, SectionHeader } from '../../design-system';
import MarketingLayout from '../layouts/MarketingLayout';
import FadeIn from '../components/FadeIn';
import ScreenPreview from '../components/ScreenPreview';
import SeoHead from '../seo/SeoHead';
import { APPLICATIONS_SEO } from '../seo/pages';

const TEMPLATES = [
  { title: 'Menus', prompt: 'Grilled salmon — market price', theme: 'coffee', category: 'Food & Beverage' },
  { title: 'Announcements', prompt: 'Closed for private event tonight', theme: 'happyHour', category: 'Operations' },
  { title: 'Hiring', prompt: 'Now hiring servers — apply today', theme: 'hiring', category: 'HR' },
  { title: 'Events', prompt: 'Trivia night — every Wednesday 7 PM', theme: 'birthday', category: 'Community' },
  { title: 'Sales', prompt: 'Buy one get one — all pizzas', theme: 'candy', category: 'Promotions' },
  { title: 'Holiday Promotions', prompt: 'Fourth of July BBQ special', theme: 'happyHour', category: 'Seasonal' },
  { title: 'Warnings', prompt: 'Wet floor — please use caution', theme: 'hiring', category: 'Safety' },
  { title: 'Emergency Notices', prompt: 'Fire drill at 2 PM today', theme: 'lakers', category: 'Safety' },
  { title: 'Birthdays', prompt: 'Happy Birthday Maria!', theme: 'birthday', category: 'Celebrations' },
  { title: 'Reviews', prompt: 'Leave us a review — scan QR code', theme: 'injera', category: 'Marketing' },
  { title: 'QR Codes', prompt: 'Scan to order online', theme: 'coffee', category: 'Marketing' },
  { title: 'Community Announcements', prompt: 'Neighborhood cleanup this Saturday', theme: 'injera', category: 'Community' },
  { title: 'Advertisements', prompt: 'Monetize your unused screen time', theme: 'lakers', category: 'Revenue', highlight: true },
];

export default function Applications() {
  return (
    <MarketingLayout>
      <SeoHead
        title={APPLICATIONS_SEO.title}
        description={APPLICATIONS_SEO.description}
        path={APPLICATIONS_SEO.path}
      />
      <section className="bg-invert-navy text-white pt-32 lg:pt-40 pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-widest text-invert-orange mb-6">
              Applications
            </p>
            <h1 className="font-interblack text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-6 max-w-4xl mx-auto">
              Restaurant menu boards, specials &amp; more
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Restaurant TV content for menus, hiring, events, and promotions — one platform for every screen.
            </p>
          </FadeIn>
        </div>
      </section>

      <Section>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {TEMPLATES.map((tmpl, i) => (
            <FadeIn key={tmpl.title} delay={i * 0.05}>
              <div
                className={`rounded-2xl border overflow-hidden hover:shadow-lg transition-all duration-300 ${
                  tmpl.highlight ? 'border-invert-orange ring-2 ring-invert-orange/20' : 'border-invert-border'
                }`}
              >
                <div className="p-4 bg-slate-50">
                  <ScreenPreview theme={tmpl.theme} animate={false} className="scale-90" />
                </div>
                <div className="p-6">
                  <span className="text-xs font-semibold uppercase tracking-wider text-invert-orange">
                    {tmpl.category}
                  </span>
                  <h3 className="text-lg font-semibold text-invert-navy mt-1 mb-2">{tmpl.title}</h3>
                  <p className="text-sm text-slate-500">
                    &ldquo;{tmpl.prompt}&rdquo;
                  </p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section className="bg-slate-50 text-center">
        <FadeIn className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-invert-navy mb-4">
            Can&apos;t find your use case?
          </h2>
          <p className="text-slate-600 mb-8">
            INVERT understands natural language. If you can describe it, INVERT can display it.
          </p>
          <Button to="/screenHostSignup" size="lg">Try It Free</Button>
        </FadeIn>
      </Section>
    </MarketingLayout>
  );
}
